import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { money, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { Badge, Card, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import type { PortalPayout } from '@/types/api'

/**
 * What actually left the Codemax bank account, against which statement, into which
 * account.
 *
 * The amount shown is the statement's. A payout stores no amount and no currency of its
 * own - that would be a derived figure in a column, free to drift from the statement it
 * settles - and it always settles one statement in full, because a statement only ever
 * contains commission already collected from the customer. One bank transfer covering two
 * statements is two rows here sharing a reference.
 */
export function Payouts() {
  const payouts = useAsync<PortalPayout[]>(
    (signal) => api.get<PortalPayout[]>('/portal/payouts', undefined, signal),
    [],
  )

  if (payouts.loading && !payouts.data) return <Loading />
  if (payouts.error) {
    return (
      <ErrorState title={payouts.error.title} detail={payouts.error.detail} onRetry={payouts.reload} />
    )
  }

  const rows = payouts.data ?? []

  return (
    <>
      <PageHeader
        title="Payouts"
        hint="Remittances Codemax has made against your statements."
      />

      <Card>
        {rows.length === 0 ? (
          <EmptyState
            title="No payouts yet"
            hint="A payout appears here once Codemax has settled one of your statements."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Paid</Th>
                <Th>Statement</Th>
                <Th>Method</Th>
                <Th>Reference</Th>
                <Th>Account</Th>
                <Th numeric>Amount</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((payout) => (
                <Tr key={payout.id}>
                  <Td className="whitespace-nowrap">{shortDate(payout.paidOn)}</Td>
                  <Td className="tabular whitespace-nowrap">
                    <Link
                      to={`/statements/${payout.statementId}`}
                      className="text-accent hover:underline"
                    >
                      {payout.statementReference}
                    </Link>
                    {/* A reversal is a second row carrying the negative, never an edit to
                        the first. Labelled, so a partner reading two rows against one
                        statement is not left working out what happened. */}
                    {payout.isReversal && (
                      <span className="ml-2">
                        <Badge tone="warning">Reversed</Badge>
                      </span>
                    )}
                  </Td>
                  <Td>{payout.method ?? <span className="text-faint">&mdash;</span>}</Td>
                  <Td className="tabular">
                    {payout.paymentReference ?? <span className="text-faint">&mdash;</span>}
                  </Td>
                  <Td className="tabular whitespace-nowrap">
                    {/* Four digits, and there is no route on this API that returns more.
                        The full number is masked before it leaves the server. */}
                    {payout.accountLastFour ? (
                      <>&bull;&bull;&bull;&bull; {payout.accountLastFour}</>
                    ) : (
                      <span className="text-faint">&mdash;</span>
                    )}
                  </Td>
                  <Td numeric className="whitespace-nowrap">
                    {money(payout.amount, payout.currencyCode)}{' '}
                    <span className="text-[11px] text-faint">{payout.currencyCode}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}
